import React, { useState, useMemo } from 'react';
import { useData } from '../../hooks/useData';
import { Typography, CircularProgress, Box } from '@mui/material';
import { DynamicIcon } from '../../components/icons';

const TableSchemaView = ({ table }) => (
    <div className="mt-2 pl-6">
        <div className="overflow-x-auto">
            <table className="w-full text-xs bg-gray-50 dark:bg-gray-800">
                <thead className="bg-gray-100 dark:bg-gray-700">
                    <tr>
                        <th className="p-2 text-left font-semibold">Column</th>
                        <th className="p-2 text-left font-semibold">Type</th>
                        <th className="p-2 text-left font-semibold">Nullable</th>
                        <th className="p-2 text-left font-semibold">Key</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                    {table.columns.map(col => (
                        <tr key={col.name}>
                            <td className="p-2 font-mono">{col.name}</td>
                            <td className="p-2 font-mono">{col.type}</td>
                            <td className="p-2">{col.is_nullable ? 'YES' : 'NO'}</td>
                            <td className="p-2">{col.key}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    </div>
);

const TenantDataExplorerPage = ({ currentUser }) => {
    // --- THIS IS THE FIX ---
    // The useData hook is now pointed to the correct, tenant-scoped endpoint.
    const { data, loading: structureLoading, error } = useData('/tenant-portal/database-explorer');

    const [expandedDb, setExpandedDb] = useState(null);
    const [expandedTable, setExpandedTable] = useState(null);

    // The returned data will be an array with 0 or 1 items, so we can get the first one.
    const tenantDatabase = useMemo(() => {
        if (!data || data.length === 0) return null;
        return data[0];
    }, [data]);


    const toggleDb = (dbName) => {
        setExpandedDb(prev => (prev === dbName ? null : dbName));
        setExpandedTable(null);
    };

    const toggleTable = (tableName) => {
        setExpandedTable(prev => (prev === tableName ? null : tableName));
    };

    if (structureLoading) return <div className="flex justify-center mt-10"><CircularProgress /></div>;
    if (error) return <div className="p-4 text-red-700 bg-red-100 rounded-md">Error: {error.message}</div>;

    if (!tenantDatabase) {
        return (
            <div>
                <Typography variant="h4" gutterBottom>Data Explorer</Typography>
                <div className="flex flex-col justify-center items-center h-64 text-gray-500">
                    <DynamicIcon name="Database" className="w-12 h-12 mb-4 opacity-50" />
                    <Typography>No provisioned database found for this organization.</Typography>
                </div>
            </div>
        );
    }

    // The rest of the component renders the single database found for the tenant
    const db = tenantDatabase;

    return (
        <div className="p-6">
            <Typography variant="h4" gutterBottom>Data Explorer</Typography>
            <Typography variant="body1" paragraph>Explore the schemas and tables of the database available to your organization.</Typography>

            <div className="space-y-4">
                <div key={db.db_name} className="bg-white dark:bg-gray-800 rounded-lg shadow-md">
                    <button onClick={() => toggleDb(db.db_name)} className="w-full p-4 flex justify-between items-center text-left">
                        <div className="flex items-center gap-4">
                            <DynamicIcon name="Database" className="w-8 h-8 text-primary" />
                            <div>
                                <h3 className="font-bold text-lg text-gray-800 dark:text-gray-100">{db.db_name}</h3>
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                    User: <span className="font-mono">{db.db_user}</span>
                                </p>
                            </div>
                        </div>
                        <DynamicIcon name={expandedDb === db.db_name ? 'ChevronUp' : 'ChevronDown'} className="w-5 h-5" />
                    </button>
                    {expandedDb === db.db_name && (
                        <div className="p-4 border-t dark:border-gray-700">
                            <h4 className="font-semibold mb-2">Tables ({db.tables.length})</h4>
                            {db.tables.length > 0 ? (
                                <ul className="space-y-1">
                                    {db.tables.map(table => (
                                        <li key={table.name}>
                                            <button onClick={() => toggleTable(table.name)} className="w-full p-2 text-left rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 flex justify-between items-center">
                                                <span>{table.name}</span>
                                                <DynamicIcon name={expandedTable === table.name ? 'ChevronUp' : 'ChevronDown'} className="w-4 h-4" />
                                            </button>
                                            {expandedTable === table.name && <TableSchemaView table={table} />}
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <div className="text-center py-4 text-gray-500">No tables found in this database.</div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TenantDataExplorerPage;